// Session storage — token, user, and active branch.
//
// These read and write the SAME localStorage keys as the legacy src/api.js.
// That is deliberate and load-bearing during the migration: one app, one
// session. A screen migrated to the typed client and a screen still on the
// old helper must see the same login and the same active branch, or switching
// branch on a new screen would silently not apply to an old one.
//
// When the last legacy screen is gone, src/api.js is deleted and this file is
// unchanged.

const TOKEN_KEY = 'hms_auth_token';
const USER_KEY = 'hms_auth_user';
const BRANCH_KEY = 'hms_active_branch_id';

/** Branch grant returned by /auth/login and /auth/me. */
export interface Branch {
  id: number;
  code: string;
  name: string;
  gstin: string | null;
  is_default?: boolean;
}

export interface SessionUser {
  id: number;
  username: string;
  full_name: string;
  role: string;
  /** Nav module ids granted to this user's role — drives sidebar visibility. */
  modules?: string[];
  active_branch?: Branch;
  branches?: Branch[];
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getAuthUser(): SessionUser | null {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null') as SessionUser | null;
  } catch {
    return null;
  }
}

export function setAuthSession(token: string, user: SessionUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (user.active_branch?.id) setActiveBranchId(user.active_branch.id);
}

export function clearAuthSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(BRANCH_KEY);
}

/**
 * The branch this user is currently acting in, sent as X-Branch-Id on every
 * authenticated request.
 *
 * This is a *preference*, never a trust boundary: the server re-validates it
 * against the user's user_branches grants on every request and returns 403 for
 * a branch they hold no grant for (see backend/middleware/auth.js). Which is
 * exactly why switching branch needs no re-login.
 */
export function getActiveBranchId(): number | null {
  const raw = localStorage.getItem(BRANCH_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isInteger(n) ? n : null;
}

export function setActiveBranchId(branchId: number): void {
  localStorage.setItem(BRANCH_KEY, String(branchId));
}

/**
 * The kiosk (?kiosk=1&token=...) has no staff login — it authenticates as
 * "the kiosk terminal" via a shared device token read once from its own URL.
 * Every other page's URL has no ?token=, so this is null everywhere else.
 */
export const KIOSK_TOKEN: string | null = new URLSearchParams(window.location.search).get('token');
